import {useMutation, useQueryClient} from '@tanstack/react-query';
import TournamentRestClient from '@/Api/TournamentRestClient';
import Environment from '../../../environment';
import type {ScheduleTournamentResponse} from './types';

export type ClubTournamentType = 'Circlechess_Online' | 'Circlechess_Arena';
export type ScheduleTournamentInput = {
  type: ClubTournamentType;
  name: string;
  organizer: string; // club name
  initialTime: number; // seconds
  incrementTime: number; // seconds
  startDate: string; // ISO — REQUIRED for Online (see note); include for both
  rounds?: number; // Online: required; Arena: omit/0
  durationMinutes?: number; // Arena: 10..720
  clubId: number; // Caissa club id
};

export function buildTournamentPayload(v: ScheduleTournamentInput) {
  const base: any = {
    source: 'caissa',
    caissa_club_id: v.clubId,
    tournament_type: v.type,
    name: v.name,
    organizer: v.organizer,
    initial_time: v.initialTime,
    increment_time: v.incrementTime,
    start_date: v.startDate, // server field for the Online builder is start_date/startDate — SEND startDate
    startDate: v.startDate, // formTournamentValidator/builder reads startDate; send both to be safe
  };
  if (v.type === 'Circlechess_Online') {
    base.rounds = v.rounds ?? 1;
  }
  if (v.type === 'Circlechess_Arena') {
    base.duration_minutes = v.durationMinutes ?? 60;
  }
  return base;
}

export function parseScheduleResponse(r: ScheduleTournamentResponse) {
  if (!r || r.status !== true || !r.data) {
    throw new Error(r?.message || 'Tournament create failed');
  }
  return r.data; // {id, name}
}

export default function useScheduleClubTournament() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (v: ScheduleTournamentInput) => {
      const res = await TournamentRestClient.post(
        '/tournament/caissa-create',
        buildTournamentPayload(v),
        {headers: {'x-api-key': Environment.X_API_KEY}}, // RUNTIME-VERIFY: x-api-key must match ccm-server config.XapiKey
      );
      return parseScheduleResponse(res.data as ScheduleTournamentResponse);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        'clubs',
        'active-tournament',
        variables.clubId,
      ]);
      queryClient.invalidateQueries(['clubs', 'activities', variables.clubId]);
    },
  });
}
